// ---------------------------------------------------------------------------
// Video Embed Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";
import { resolveVideoEmbed, type VideoEmbed } from "@ease/shared/video-embed";

interface VideoEmbedProps {
  videoUrl: string;
  heading?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  autoplay?: boolean;
  thumbnail?: string;
}

const ASPECT_PADDING: Record<string, string> = {
  "16:9": "56.25%",
  "4:3": "75%",
  "1:1": "100%",
};

function appendAutoplay(embedUrl: string, autoplay: boolean): string {
  if (!autoplay) return embedUrl;
  const parsed = new URL(embedUrl);
  parsed.searchParams.set("autoplay", "1");
  return parsed.toString();
}

function parseEmbed(rawUrl: string, autoplay: boolean): VideoEmbed | null {
  if (!rawUrl) return null;
  const embed = resolveVideoEmbed(rawUrl);
  return embed ? { ...embed, embedUrl: appendAutoplay(embed.embedUrl, autoplay) } : null;
}

function isDirectVideoUrl(rawUrl: string): boolean {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === "https:" && /\.(mp4|webm|ogg|mov)$/i.test(parsed.pathname);
  } catch {
    return false;
  }
}

function renderUnavailableVideo(title: string): string {
  return `<div class="ease-video-unavailable" role="status" aria-label="${attr(`${title} unavailable`)}" style="display: flex; align-items: center; justify-content: center; min-height: 220px; border-radius: 16px; background: var(--ease-color-muted, #f3f4f6); color: var(--ease-color-muted-foreground, #6b7280); text-align: center; padding: 24px;">
    Video embed unavailable. Use a supported public URL or a Vimeo URL with its private hash.
  </div>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as VideoEmbedProps;

  const videoUrl = p.videoUrl ?? "";
  const aspectRatio = p.aspectRatio ?? "16:9";
  const autoplay = p.autoplay === true;
  const padding = ASPECT_PADDING[aspectRatio] ?? "56.25%";
  const title = p.heading ?? "Video";

  const headingHtml = p.heading
    ? `<h2 class="ease-video-heading">${text(p.heading)}</h2>\n  `
    : "";

  const embed = parseEmbed(videoUrl, autoplay);

  if (embed) {
    const sandboxAttr = embed.isSandboxed ? ' sandbox="allow-scripts allow-same-origin allow-presentation"' : "";
    return `<section class="ease-section ease-video-embed" aria-label="${attr(title)}">
  ${headingHtml}<div class="ease-video-container" style="position: relative; width: 100%; padding-bottom: ${attr(padding)}; height: 0; overflow: hidden;">
    <iframe src="${url(embed.embedUrl)}" title="${attr(title)}" loading="lazy" allow="autoplay; encrypted-media"${sandboxAttr} allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"></iframe>
  </div>
</section>`;
  }

  if (!isDirectVideoUrl(videoUrl)) {
    return `<section class="ease-section ease-video-embed" aria-label="${attr(title)}">
  ${headingHtml}${renderUnavailableVideo(title)}
</section>`;
  }

  // Fallback: direct video URL
  return `<section class="ease-section ease-video-embed" aria-label="${attr(title)}">
  ${headingHtml}<div class="ease-video-container" style="position: relative; width: 100%; padding-bottom: ${attr(padding)}; height: 0; overflow: hidden;">
    <video src="${url(videoUrl)}" title="${attr(title)}" controls ${autoplay ? "autoplay muted" : ""} loading="lazy" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
      Your browser does not support video playback.
    </video>
  </div>
</section>`;
}

registerRenderer("video-embed", { render });
